import { useCallback, useState, useEffect, useRef } from 'react';
import { Head, Link, usePage } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { Breadcrumbs } from '../../components/products/Breadcrumbs';
import { SearchBar } from '../../components/products/SearchBar';
import { CategoryTabs } from '../../components/products/CategoryTabs';
import { ProductGrid } from '../../components/products/ProductGrid';
import { ProductCard } from '../../components/products/ProductCard';
import { RelatedChips } from '../../components/products/RelatedChips';
import { RelatedProductsSlider } from '../../components/products/RelatedProductsSlider';
import { UnavailableProductCard } from '../../components/products/UnavailableProductCard';
import { MobileToolbar } from '../../components/products/MobileToolbar';
import { CategoryIcon } from '../../components/products/categoryIcons';
import { KeyboardShortcutsModal } from '../../components/products/KeyboardShortcutsModal';
import { KeyboardHint } from '../../components/products/KeyboardHint';
import { useInfiniteProducts } from '../../hooks/useInfiniteProducts';
import { useProductSearch } from '../../hooks/useProductSearch';
import { useGridKeyboardNav } from '../../hooks/useGridKeyboardNav';
import type { ProductCardHandle } from '../../hooks/useGridKeyboardNav';
import { getCSRFToken } from '../../utils/csrf';
import { dispatchCartCount } from '../../utils/cartBadge';
import { route } from '../../utils/route';
import { gtagAsync, trackViewProductList } from '../../utils/tracking';
import type { ProductsPageProps } from '../../types/products';

export default function Products({
  category,
  categories,
  products: initialProducts,
  totalProducts,
  currentPage,
  totalPages,
  perPage,
  search,
  searchData,
  structuredDataJson,
  faqSchemaJson,
}: ProductsPageProps) {
  const { defaultCategorySlug } = usePage<{ defaultCategorySlug: string }>().props;
  const { products, isLoadingMore, hasMore, sentinelRef } = useInfiniteProducts({
    initialProducts,
    totalProducts,
    perPage,
    currentPage,
    categorySlug: category.slug,
  });

  const {
    searchTerm, setSearchTerm, submitSearch, clearSearch, searchResults, isSearching,
    isLoadingSearch, searchHasMore, searchTotal, searchRelated, searchRelatedContext,
    searchUnavailable, searchSentinelRef, semanticEnabled, setSemanticEnabled,
  } = useProductSearch({ initialSearch: search, initialSearchData: searchData });

  // Category change flash — show on mount if navigated from another category
  const [flash, setFlash] = useState<string | null>(() => {
    try {
      const prev = sessionStorage.getItem('products_category');
      sessionStorage.setItem('products_category', category.slug);
      if (prev && prev !== category.slug) return category.slug;
    } catch {}
    return null;
  });
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 1500);
    return () => clearTimeout(t);
  }, [flash]);

  // Track product list views
  useEffect(() => {
    if (!isSearching) {
      trackViewProductList(`category:${category.slug}`, totalProducts);
    }
  }, [category.slug, totalProducts, isSearching]);

  const handleAddToCart = useCallback(async (productId: number, formatId: number, quantity: number) => {
    const response = await fetch(route('api.cart.add_item'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCSRFToken(),
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: productId, format_id: formatId, quantity }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Error al agregar al carrito');
    }

    const data = await response.json();

    // GA4 tracking (async to avoid blocking INP)
    gtagAsync('event', 'add_to_cart', {
      currency: 'ARS',
      items: [{ item_id: productId, quantity }],
    });

    // Update cart badge via event (Navbar listens to 'cart-updated')
    if (data.summary?.count != null) {
      dispatchCartCount(data.summary.count);
    }
  }, []);

  // When searching, show search results; otherwise show category products
  const displayProducts = isSearching ? searchResults : products;
  const displayHasMore = isSearching ? searchHasMore : hasMore;
  const displayLoading = isSearching ? isLoadingSearch : isLoadingMore;
  const displaySentinelRef = isSearching ? searchSentinelRef : sentinelRef;

  // Keyboard navigation state
  const gridRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const cardHandlesRef = useRef<Map<number, ProductCardHandle>>(new Map());
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isInnerInputFocused, setIsInnerInputFocused] = useState(false);

  // Desktop-only: enable keyboard nav on sm+ screens
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 640;

  const { focusedIndex, setFocusedIndex, isHelpOpen, setIsHelpOpen } = useGridKeyboardNav({
    enabled: isDesktop,
    itemCount: displayProducts.length,
    gridRef,
    searchInputRef,
    cardHandlesRef,
    isSearchFocused,
    isInnerInputFocused,
    sentinelRef: displaySentinelRef,
    hasMore: displayHasMore,
  });

  // Reset focus when search/category changes
  useEffect(() => {
    setFocusedIndex(null);
  }, [category.slug, isSearching, setFocusedIndex]);

  const pageUrl = route('products.index', { cat: category.slug });
  const pageTitle = `${category.name} - Productos | Abasto Virtual`;

  return (
    <MainLayout>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={`Comprá ${category.name.toLowerCase()} online con delivery en Córdoba. Venta mayorista y minorista. Productos frescos de calidad.`} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={currentPage > 1 ? `${pageUrl}?page=${currentPage}` : pageUrl} />
        {currentPage > 1 && (
          <link rel="prev" href={currentPage === 2 ? pageUrl : `${pageUrl}?page=${currentPage - 1}`} />
        )}
        {currentPage < totalPages && (
          <link rel="next" href={`${pageUrl}?page=${currentPage + 1}`} />
        )}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:locale" content="es_AR" />
        <meta property="og:site_name" content="Abasto Virtual" />
        <script type="application/ld+json">{JSON.stringify(structuredDataJson)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchemaJson)}</script>
        <link rel="alternate" type="text/plain" href={`/productos/${category.slug}.md`} title="LLM Context" />
      </Head>

      <div className="mx-auto max-w-6xl px-4 py-6 pb-24 sm:pb-6">
        <Breadcrumbs
          items={[
            { name: 'Inicio', url: route('index') },
            { name: 'Productos', url: route('products.index', { cat: defaultCategorySlug }) },
          ]}
          currentPage={category.name}
        />

        {/* Desktop search bar (mobile uses floating toolbar) */}
        <div className="hidden sm:flex sm:items-center sm:gap-3">
          <div className="flex-1">
            <SearchBar
              ref={searchInputRef}
              value={searchTerm}
              onChange={setSearchTerm}
              onSubmit={submitSearch}
              onClear={clearSearch}
              onFocusChange={setIsSearchFocused}
              semanticEnabled={semanticEnabled}
              onSemanticToggle={setSemanticEnabled}
            />
          </div>
          <div className="mb-4">
            <KeyboardHint />
          </div>
        </div>

        {/* Hide category tabs when searching */}
        {!isSearching && (
          <CategoryTabs
            categories={categories}
            activeCategoryId={category.id}
          />
        )}

        {category.slug === 'verduleria' && !isSearching && (
          <p className="mb-4 text-sm text-gray-700">
            <Link href="/bolson-de-verduras" className="font-semibold text-green-700 underline hover:text-green-800">
              Bolsón de verduras: qué trae, cuánto dura y cuánto sale puesto en casa
            </Link>
          </p>
        )}

        {/* Case 3: No results — show unavailable matches + alternatives */}
        {isSearching && searchRelatedContext === 'no_results' && searchUnavailable.length > 0 ? (
          <div>
            <ProductGrid
              ref={gridRef}
              products={displayProducts}
              onAddToCart={handleAddToCart}
              isLoadingMore={displayLoading}
              hasMore={displayHasMore}
              sentinelRef={displaySentinelRef}
              isSearching={isSearching}
              searchTotal={searchTotal}
              focusedIndex={focusedIndex}
              cardHandlesRef={cardHandlesRef}
              onInnerInputFocusChange={setIsInnerInputFocused}
              hasUnavailableMatches
            />
            <div className="py-8">
              <p className="mb-3 text-sm text-gray-500">
                Encontramos {searchUnavailable.length === 1 ? 'este producto' : 'estos productos'}, pero ya no {searchUnavailable.length === 1 ? 'está disponible' : 'están disponibles'}:
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {searchUnavailable.map(p => (
                  <UnavailableProductCard key={p.id} product={p} />
                ))}
              </div>
              {searchRelated.length > 0 && (
                <div className="mt-6">
                  <h2 className="mb-3 text-sm font-medium text-gray-600">
                    Te recomendamos estas alternativas
                  </h2>
                  <div className="grid grid-cols-1 gap-3 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {searchRelated.map(product => (
                      <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : isSearching && searchRelatedContext === 'many_results' && searchResults.length >= 5 && searchRelated.length > 0 ? (
          /* Case 2 (≥5 results): Split grid with slider interleaved */
          <div>
            {/* Count header */}
            {searchTotal > 0 && (
              <p className="mb-3 text-sm text-gray-500">
                {searchTotal} {searchTotal === 1 ? 'producto encontrado' : 'productos encontrados'}
              </p>
            )}
            {/* First 4 products */}
            <div ref={gridRef} className="grid grid-cols-1 gap-3 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {searchResults.slice(0, 4).map((product, index) => (
                <ProductCard
                  key={product.id}
                  ref={(handle) => {
                    if (!cardHandlesRef.current) return;
                    if (handle) cardHandlesRef.current.set(index, handle);
                    else cardHandlesRef.current.delete(index);
                  }}
                  product={product}
                  onAddToCart={handleAddToCart}
                  isFocused={focusedIndex === index}
                  onInnerInputFocusChange={setIsInnerInputFocused}
                />
              ))}
            </div>
            {/* Slider */}
            <RelatedProductsSlider products={searchRelated} onAddToCart={handleAddToCart} />
            {/* Remaining products */}
            <div className="grid grid-cols-1 gap-3 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {searchResults.slice(4).map((product, index) => (
                <ProductCard
                  key={product.id}
                  ref={(handle) => {
                    const realIndex = index + 4;
                    if (!cardHandlesRef.current) return;
                    if (handle) cardHandlesRef.current.set(realIndex, handle);
                    else cardHandlesRef.current.delete(realIndex);
                  }}
                  product={product}
                  onAddToCart={handleAddToCart}
                  isFocused={focusedIndex === index + 4}
                  onInnerInputFocusChange={setIsInnerInputFocused}
                />
              ))}
            </div>
            {/* Sentinel for infinite scroll */}
            {searchHasMore && (
              <div ref={searchSentinelRef} className="flex justify-center py-8">
                {isLoadingSearch && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <i className="fa fa-spinner fa-spin" />
                    Cargando más productos...
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Default: single grid (also covers Case 1 chips + Case 2 with <5 results) */
          <div>
            <ProductGrid
              ref={gridRef}
              products={displayProducts}
              onAddToCart={handleAddToCart}
              isLoadingMore={displayLoading}
              hasMore={displayHasMore}
              sentinelRef={displaySentinelRef}
              isSearching={isSearching}
              searchTotal={searchTotal}
              focusedIndex={focusedIndex}
              cardHandlesRef={cardHandlesRef}
              onInnerInputFocusChange={setIsInnerInputFocused}
            />
            {/* Case 1: Few results — show chips */}
            {isSearching && searchRelatedContext === 'few_results' && searchRelated.length > 0 && (
              <RelatedChips products={searchRelated} />
            )}
            {/* Case 2 with 3-4 results — slider below grid */}
            {isSearching && searchRelatedContext === 'many_results' && searchRelated.length > 0 && searchResults.length < 5 && (
              <RelatedProductsSlider products={searchRelated} onAddToCart={handleAddToCart} />
            )}
          </div>
        )}

        {/* SR-only pagination links for SEO crawlers */}
        {totalPages > 1 && (
          <nav aria-label="Paginación de catálogo" className="sr-only">
            {currentPage > 1 && (
              <a href={currentPage === 2 ? pageUrl : `${pageUrl}?page=${currentPage - 1}`}>
                Anterior
              </a>
            )}
            {currentPage < totalPages && (
              <a href={`${pageUrl}?page=${currentPage + 1}`}>
                Siguiente
              </a>
            )}
          </nav>
        )}
      </div>

      {/* Mobile floating toolbar with search + categories */}
      <MobileToolbar
        categories={categories}
        activeCategoryId={category.id}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearchSubmit={submitSearch}
        onSearchClear={clearSearch}
      />

      {/* Category change flash */}
      {flash && (
        <div className="fixed inset-x-0 top-24 z-50 flex justify-center pointer-events-none animate-category-flash sm:hidden">
          <div className="flex items-center gap-2 rounded-full bg-gray-900/80 px-5 py-2.5 text-sm font-medium text-white shadow-lg backdrop-blur-sm">
            <CategoryIcon slug={flash} size={18} strokeWidth={2} />
            {category.name}
          </div>
        </div>
      )}
      <style>{`
        @keyframes category-flash {
          0% { opacity: 0; transform: translateY(-8px) scale(0.95); }
          15% { opacity: 1; transform: translateY(0) scale(1); }
          75% { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-4px) scale(0.98); }
        }
        .animate-category-flash {
          animation: category-flash 1.5s ease-out forwards;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Keyboard shortcuts modal */}
      <KeyboardShortcutsModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
    </MainLayout>
  );
}
